import React, { useState, useCallback, useRef } from 'react';
import { FileNode, FileBrowserProps, useToastStore } from '@edgerunner/shared';

// Supported file extensions - matches desktop version
const SUPPORTED_EXTENSIONS = [
  '.docx',
  '.doc',
  '.txt',
  '.log',
  '.ini',
  '.rtf',
  '.odt',
  '.xls',
  '.xlsx',
  '.csv',
  '.pptx',
  '.pdf',
  '.epub',
  '.htm',
  '.html',
  '.md',
  '.json',
  '.xml'
];

const isFileSupported = (filename: string): boolean => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return false;
  }
  const extension = filename.toLowerCase().substring(lastDotIndex);
  return SUPPORTED_EXTENSIONS.includes(extension);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

interface SelectedFile {
  file: File;
  node: FileNode;
}

function FileBrowser({ selectedCollectionId: _selectedCollectionId, onAddFile, addedFiles }: FileBrowserProps) {
  const { showToast } = useToastStore();
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [showSupportedTypes, setShowSupportedTypes] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: SelectedFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isFileSupported(file.name)) {
        // Create a FileNode-compatible object with the File object attached
        const node: FileNode = {
          name: file.name.normalize('NFC'),
          path: file.name, // Use filename as path identifier in web
          isDirectory: false,
          size: file.size,
        };
        
        newFiles.push({ file, node });
      }else {
        showToast(`Unsupported file(s)`, 'error', { timeout: 3000, adaptToModal:true });
      }
    }

    if (newFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...newFiles]);
    }
    
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [showToast]);

  // Add file to collection
  const handleAddFile = useCallback((selectedFile: SelectedFile) => {
    // Create a FileNode with the actual File object attached
    const nodeWithFile: FileNode & { file: File } = {
      ...selectedFile.node,
      file: selectedFile.file
    };
    
    onAddFile(nodeWithFile as any);
  }, [onAddFile]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Open file picker
  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="bg-neutral-600 rounded-medium overflow-hidden h-full flex flex-col">
        {/* Supported File Types Info */}
        <div className="border-b border-neutral-500">
          <button
            onClick={() => setShowSupportedTypes(!showSupportedTypes)}
            className="w-full px-3 py-2 text-left text-sm text-neutral-300 hover:text-neutral-200 hover:bg-neutral-500 transition-colors duration-200 flex items-center justify-between"
          >
            <span>Supported File Types</span>
            <i className={`fas fa-chevron-${showSupportedTypes ? 'up' : 'down'} text-xs`}></i>
          </button>
          {showSupportedTypes && (
            <div className="px-3 py-3 text-md text-neutral-200">
              <div className="grid grid-cols-4 gap-1">
                {SUPPORTED_EXTENSIONS.map((ext) => (
                  <span key={ext} className="bg-neutral-500 px-2 py-1 rounded text-center">
                    {ext}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-neutral-300 text-xs">Only these file types can be added to collections.</p>
            </div>
          )}
        </div>

        {/* File Upload Area */}
        <div className="flex-1 overflow-y-auto p-3 bg-neutral-700">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORTED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Browse Files Button */}
          <div className="text-center p-3">
            <button
                onClick={handleBrowseClick}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-red-900/50 transform hover:scale-105"
              >
                <i className="fas fa-folder-open mr-2"></i>
                Browse Files
            </button>
          </div>

          {/* Empty State */}
          {selectedFiles.length === 0 && (
            <div className="text-center text-neutral-400">
              <i className="fas fa-inbox text-3xl mb-3 opacity-50"></i>
              <p className="text-sm">No files selected</p>
            </div>
          )}

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="mt-1">
              <h3 className="text-sm font-medium text-neutral-300 mb-2 px-2">
                Selected Files ({selectedFiles.length})
              </h3>
              <div className="space-y-1">
                {selectedFiles.map((selectedFile, index) => (
                  <div
                    key={`${selectedFile.node.path}-${index}`}
                    className="flex items-center py-2 px-2 hover:bg-neutral-600 rounded-small transition-colors duration-200"
                  >
                    <span className="mr-2">
                      <i className="fas fa-file text-red-400" style={{ fontSize: '16px' }}></i>
                    </span>
                    <span className="flex-1 truncate min-w-0 text-neutral-200" title={selectedFile.node.name}>
                      {selectedFile.node.name}
                    </span>
                    <span className="text-sm text-neutral-200 mr-3 flex-shrink-0">
                      {formatFileSize(selectedFile.node.size || 0)}
                    </span>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="px-2 py-1 text-sm rounded-small bg-neutral-500 text-neutral-300 hover:bg-neutral-400 hover:text-neutral-100 transition-colors duration-200 flex-shrink-0 mr-2"
                      title="Remove from selection"
                      aria-label="Remove file selection"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                    <button
                      onClick={() => handleAddFile(selectedFile)}
                      disabled={addedFiles.has(selectedFile.node.path)}
                      className={`px-3 py-1 text-sm rounded-small transition-colors duration-200 flex-shrink-0 ${
                        addedFiles.has(selectedFile.node.path)
                          ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                          : 'bg-red-600 text-neutral-100 hover:bg-red-500'
                      }`}
                    >
                      {addedFiles.has(selectedFile.node.path) ? 'Added' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Bulk Actions */}
              <div className="mt-3 pt-3 border-t border-neutral-600 flex justify-between items-center px-2">
                <button
                  onClick={() => setSelectedFiles([])}
                  className="px-3 py-1 text-sm rounded-small bg-neutral-500 text-neutral-300 hover:bg-neutral-400 transition-colors duration-200"
                >
                  <i className="fas fa-times mr-2"></i>
                  Clear All
                </button>
                <button
                  onClick={() => {
                    selectedFiles.forEach(sf => {
                      if (!addedFiles.has(sf.node.path)) {
                        handleAddFile(sf);
                      }
                    });
                  }}
                  disabled={selectedFiles.every(sf => addedFiles.has(sf.node.path))}
                  className={`px-3 py-1 text-sm rounded-small transition-colors duration-200 ${
                    selectedFiles.every(sf => addedFiles.has(sf.node.path))
                      ? 'bg-neutral-500 text-neutral-300 cursor-not-allowed'
                      : 'bg-red-600 text-neutral-100 hover:bg-red-500'
                  }`}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Add All ({selectedFiles.filter(sf => !addedFiles.has(sf.node.path)).length})
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FileBrowser;
